import React, { useState, useRef } from 'react';
import './InteractiveGallery.css';

interface GalleryImage {
  src: string;
  thumbnail: string;
  title: string;
  description: string;
  category: string;
}

interface GalleryProps {
  layout?: 'grid' | 'masonry' | 'showcase';
}

// Lazy loading image component - uses thumbnail in grid
const LazyImage: React.FC<{ thumbnail: string; alt: string; description: string; onClick: () => void }> = ({ thumbnail, alt, description, onClick }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  return (
    <div className="gallery-card" onClick={onClick} ref={cardRef}>
      <div className="gallery-card-image">
        {!isLoaded && <div className="image-skeleton"></div>}
        <img
          src={thumbnail}
          alt={alt}
          loading="lazy"
          onLoad={() => setIsLoaded(true)}
          style={{
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
        <div className="gallery-card-overlay">
          <div className="gallery-card-overlay-content">
            <h3>{alt}</h3>
            <p>{description}</p>
            <span className="view-icon">👁️ View</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const InteractiveGallery: React.FC<GalleryProps> = ({ layout = 'grid' }) => {
  // Build gallery images from photo manifest
  const galleryImages: GalleryImage[] = [
    // Exterior - select key images
    { src: process.env.PUBLIC_URL + '/images/exterior/mb_exterior_summer.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/exterior/mb_exterior_summer.jpg', title: 'Lodge Exterior', description: 'Summer view of the historic lodge', category: 'Exterior' },
    { src: process.env.PUBLIC_URL + '/images/exterior/mb_exterior_sign.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/exterior/mb_exterior_sign.jpg', title: 'Welcome Sign', description: 'Molly Butler Lodge entrance', category: 'Exterior' },
    { src: process.env.PUBLIC_URL + '/images/exterior/MB_exterior_snow.PNG', thumbnail: process.env.PUBLIC_URL + '/thumbnails/exterior/MB_exterior_snow.jpg', title: 'Winter Wonderland', description: 'Snow-covered lodge', category: 'Exterior' },
    { src: process.env.PUBLIC_URL + '/images/exterior/0928.25-.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/exterior/0928.25-.jpg', title: 'Front View', description: 'Main entrance view', category: 'Exterior' },
    { src: process.env.PUBLIC_URL + '/images/exterior/0928.25--3.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/exterior/0928.25--3.jpg', title: 'Lodge Architecture', description: 'Historic building details', category: 'Exterior' },
    { src: process.env.PUBLIC_URL + '/images/exterior/EXT-.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/exterior/EXT-.jpg', title: 'Exterior Detail', description: 'Architectural features', category: 'Exterior' },

    // Interior - dining and bar areas
    { src: process.env.PUBLIC_URL + '/images/interior/mb_interior_bar.png', thumbnail: process.env.PUBLIC_URL + '/thumbnails/interior/mb_interior_bar.jpg', title: 'Historic Bar', description: 'Rustic bar area', category: 'Interior' },
    { src: process.env.PUBLIC_URL + '/images/interior/mb_interior_dining.png', thumbnail: process.env.PUBLIC_URL + '/thumbnails/interior/mb_interior_dining.jpg', title: 'Main Dining Room', description: 'Warm dining atmosphere', category: 'Interior' },
    { src: process.env.PUBLIC_URL + '/images/interior/mb_interior_lobby.png', thumbnail: process.env.PUBLIC_URL + '/thumbnails/interior/mb_interior_lobby.jpg', title: 'Cozy Lobby', description: 'Welcome area', category: 'Interior' },
    { src: process.env.PUBLIC_URL + '/images/interior/INT-.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/interior/INT-.jpg', title: 'Dining Space', description: 'Restaurant interior', category: 'Interior' },
    { src: process.env.PUBLIC_URL + '/images/interior/INT--7.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/interior/INT--7.jpg', title: 'Interior Detail', description: 'Lodge atmosphere', category: 'Interior' },
    { src: process.env.PUBLIC_URL + '/images/interior/INT--12.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/interior/INT--12.jpg', title: 'Seating Area', description: 'Comfortable dining', category: 'Interior' },

    // Food - select best dishes
    { src: process.env.PUBLIC_URL + '/images/food/MBL_Food_Select-007.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/food/MBL_Food_Select-007.jpg', title: 'Hearty Chili', description: 'House specialty', category: 'Food' },
    { src: process.env.PUBLIC_URL + '/images/food/MBL_Food_Select-013.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/food/MBL_Food_Select-013.jpg', title: 'Fresh Cobb Salad', description: 'Garden fresh ingredients', category: 'Food' },
    { src: process.env.PUBLIC_URL + '/images/food/MBL_Food_Select-062.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/food/MBL_Food_Select-062.jpg', title: 'Signature Dish', description: 'Chef\'s specialty', category: 'Food' },
    { src: process.env.PUBLIC_URL + '/images/food/MBL_Food_Select-072.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/food/MBL_Food_Select-072.jpg', title: 'Featured Entree', description: 'Daily special', category: 'Food' },
    { src: process.env.PUBLIC_URL + '/images/food/MBL_Food_Select-089.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/food/MBL_Food_Select-089.jpg', title: 'Comfort Food', description: 'Mountain cuisine', category: 'Food' },
    { src: process.env.PUBLIC_URL + '/images/food/MBL_Food_Select-096.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/food/MBL_Food_Select-096.jpg', title: 'Delicious Fare', description: 'Home-style cooking', category: 'Food' },

    // Drinks - cocktails and beverages
    { src: process.env.PUBLIC_URL + '/images/drinks/MBL_Booze_Select-0526.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/drinks/MBL_Booze_Select-0526.jpg', title: 'Signature Cocktails', description: 'Handcrafted beverages', category: 'Drinks' },
    { src: process.env.PUBLIC_URL + '/images/drinks/MBL_Booze_Select-0530.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/drinks/MBL_Booze_Select-0530.jpg', title: 'Craft Drinks', description: 'Bar specialties', category: 'Drinks' },
    { src: process.env.PUBLIC_URL + '/images/drinks/MBL_Booze_Select-0535.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/drinks/MBL_Booze_Select-0535.jpg', title: 'Refreshments', description: 'Unique cocktails', category: 'Drinks' },
    { src: process.env.PUBLIC_URL + '/images/drinks/MBL_Booze_Select-0545.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/drinks/MBL_Booze_Select-0545.jpg', title: 'Bar Selection', description: 'Premium drinks', category: 'Drinks' },

    // Lifestyle - guest experience
    { src: process.env.PUBLIC_URL + '/images/lifestyle/MBL_LS-0352.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/lifestyle/MBL_LS-0352.jpg', title: 'Lodge Experience', description: 'Memorable moments', category: 'Lifestyle' },
    { src: process.env.PUBLIC_URL + '/images/lifestyle/MBL_LS-0360.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/lifestyle/MBL_LS-0360.jpg', title: 'Guest Enjoyment', description: 'Mountain getaway', category: 'Lifestyle' },
    { src: process.env.PUBLIC_URL + '/images/lifestyle/MBL_LS-0371.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/lifestyle/MBL_LS-0371.jpg', title: 'Relaxation', description: 'Peaceful atmosphere', category: 'Lifestyle' },
    { src: process.env.PUBLIC_URL + '/images/lifestyle/MBL_LS-0394.jpg', thumbnail: process.env.PUBLIC_URL + '/thumbnails/lifestyle/MBL_LS-0394.jpg', title: 'Mountain Living', description: 'White Mountains lifestyle', category: 'Lifestyle' },

    // Rooms - accommodations
    { src: process.env.PUBLIC_URL + '/images/rooms/interior_room_bedroom.png', thumbnail: process.env.PUBLIC_URL + '/thumbnails/rooms/interior_room_bedroom.jpg', title: 'Guest Bedroom', description: 'Comfortable accommodations', category: 'Rooms' },
    { src: process.env.PUBLIC_URL + '/images/rooms/interior_room_bathroom.png', thumbnail: process.env.PUBLIC_URL + '/thumbnails/rooms/interior_room_bathroom.jpg', title: 'Private Bathroom', description: 'Modern amenities', category: 'Rooms' },
  ];

  const categories = ['All', 'Exterior', 'Interior', 'Food', 'Drinks', 'Lifestyle', 'Rooms'];
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const filteredImages = selectedCategory === 'All'
    ? galleryImages
    : galleryImages.filter(img => img.category === selectedCategory);

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % filteredImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') nextImage();
    if (e.key === 'ArrowLeft') prevImage();
    if (e.key === 'Escape') closeLightbox();
  };

  return (
    <div className="interactive-gallery">
      {/* Category Tabs */}
      <div className="gallery-tabs">
        {categories.map(category => (
          <button
            key={category}
            className={`gallery-tab ${selectedCategory === category ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Gallery Grid */}
      <div className={`gallery-container gallery-${layout}`}>
        {filteredImages.map((image, index) => (
          <LazyImage
            key={index}
            thumbnail={image.thumbnail}
            alt={image.title}
            description={image.description}
            onClick={() => openLightbox(index)}
          />
        ))}
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div
          className="lightbox-overlay"
          onClick={closeLightbox}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          <button className="lightbox-close" onClick={closeLightbox}>✕</button>
          <button className="lightbox-prev" onClick={(e) => { e.stopPropagation(); prevImage(); }}>‹</button>
          <button className="lightbox-next" onClick={(e) => { e.stopPropagation(); nextImage(); }}>›</button>

          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={filteredImages[currentImageIndex].src}
              alt={filteredImages[currentImageIndex].title}
            />
            <div className="lightbox-info">
              <h3>{filteredImages[currentImageIndex].title}</h3>
              <p>{filteredImages[currentImageIndex].description}</p>
              <span className="lightbox-counter">
                {currentImageIndex + 1} / {filteredImages.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveGallery;
